import { useState, useRef, useEffect } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import IconButton, { IconButtonProps } from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

import CopyIcon from "@/assets/copy";
import CheckIcon from "@mui/icons-material/Check";

interface ClipboardCopyProps {
  value: string;
  theme?: "dark" | "light";
  edge?: IconButtonProps["edge"];
}

const ClipboardCopy = ({ value, edge }: ClipboardCopyProps): JSX.Element => {
  const [isCopied, setIsCopied] = useState(false);
  const [_, copy] = useCopyToClipboard();
  const resetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleCopy = () => {
    copy(value)
      .then(() => {
        setIsCopied(true);

        // Ensure at least 2 seconds of "Copied" state
        resetTimeoutRef.current && clearTimeout(resetTimeoutRef.current);
        resetTimeoutRef.current = setTimeout(() => {
          // Now allow it to reset on mouse leave
          resetTimeoutRef.current = null;
        }, 2000);
      })
      .catch((err) => console.log("An error occurred while copying: ", err));
  };

  const handleMouseLeave = () => {
    if (resetTimeoutRef.current === null) {
      // Only reset after 2 seconds
      setIsCopied(false);
    } else {
      // Wait until 2s passes
      leaveTimeoutRef.current && clearTimeout(leaveTimeoutRef.current);
      leaveTimeoutRef.current = setTimeout(() => {
        setIsCopied(false);
      }, 2000); // guarantee 2s if user leaves early
    }
  };

  useEffect(() => {
    return () => {
      if (resetTimeoutRef.current) clearTimeout(resetTimeoutRef.current);
      if (leaveTimeoutRef.current) clearTimeout(leaveTimeoutRef.current);
    };
  }, []);

  return (
    <Tooltip
      title={isCopied ? "Copied to clipboard!" : "Copy"}
      sx={{ zIndex: 2 }}
    >
      <IconButton
        color="inherit"
        edge={edge}
        onClick={handleCopy}
        onMouseLeave={handleMouseLeave}
      >
        {isCopied ? (
          <CheckIcon sx={{ height: 16, width: 16 }} />
        ) : (
          <CopyIcon sx={{ height: 16, width: 16 }} />
        )}
      </IconButton>
    </Tooltip>
  );
};

export { ClipboardCopy };
